// MarketingLayout.tsx — Phase 1 of the Marketing UX overhaul. Replaces the
// 13-tab horizontal sub-nav with a grouped sidebar (Overview + 5 funnel groups:
// Campaigns, Contacts, Intelligence, Capture, Content). Renders once as a route
// layout; each marketing page renders into the <Outlet/>. Responsive: the
// sidebar collapses to a horizontal scroll on narrow screens (see styles.css
// .mkt-* @media). Keeps the `sub-nav-marketing` testid for continuity.

import { Fragment } from 'react'
import { Link, Outlet, useLocation, useParams } from 'react-router-dom'

import { Icon } from './Icon'

const OAKRIDGE_ID = '00000000-0a17-0000-0000-000000000001'

interface NavItem {
  id: string
  label: string
  icon: string
}
interface NavGroup {
  label: string | null
  items: NavItem[]
}

const GROUPS: NavGroup[] = [
  { label: null, items: [{ id: 'overview', label: 'Overview', icon: 'grid' }] },
  {
    label: 'Campaigns',
    items: [
      { id: 'campaigns', label: 'Campaigns', icon: 'send' },
    ],
  },
  {
    label: 'Contacts',
    items: [
      { id: 'contacts', label: 'Contacts', icon: 'users' },
      { id: 'lists', label: 'Smart lists', icon: 'list' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { id: 'lead-scores', label: 'Lead scores', icon: 'zap' },
      { id: 'reactivation', label: 'Reactivation', icon: 'refresh' },
      { id: 'partners', label: 'Partner network', icon: 'share' },
    ],
  },
  {
    label: 'Capture',
    items: [
      { id: 'leads', label: 'Website leads', icon: 'globe' },
      { id: 'lots', label: 'Lot prospects', icon: 'map-pin' },
    ],
  },
  {
    label: 'Content',
    items: [
      { id: 'feed', label: 'Publish feed', icon: 'image' },
      { id: 'gbp', label: 'Google Business', icon: 'star' },
      { id: 'testimonials', label: 'Testimonials', icon: 'message' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { id: 'email', label: 'Email health', icon: 'activity' },
      { id: 'sweep', label: 'Gmail sweep', icon: 'mail' },
      { id: 'import', label: 'Import', icon: 'upload' },
    ],
  },
]

export function MarketingLayout(): JSX.Element {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>()
  const projectId = routeProjectId ?? OAKRIDGE_ID
  const { pathname } = useLocation()
  const active = pathname.match(/\/marketing\/([^/]+)/)?.[1] ?? 'overview'

  return (
    <div className="mkt-layout">
      <nav className="mkt-sidebar" data-testid="sub-nav-marketing" aria-label="Marketing sections">
        {GROUPS.map((g, i) => (
          <Fragment key={g.label ?? `group-${i}`}>
            {g.label ? <div className="mkt-nav-group">{g.label}</div> : null}
            {g.items.map((it) => (
              <Link
                key={it.id}
                to={`/projects/${projectId}/marketing/${it.id}`}
                className={`mkt-nav-item${active === it.id ? ' active' : ''}`}
                data-testid={`subnav-marketing-${it.id}`}
                aria-current={active === it.id ? 'page' : undefined}
              >
                <Icon name={it.icon} size={17} />
                {it.label}
              </Link>
            ))}
          </Fragment>
        ))}
      </nav>
      <div className="mkt-content">
        <Outlet />
      </div>
    </div>
  )
}
